/**
 * DateRangePicker Component - Custom date range with quick presets
 */
import React, { useState } from 'react';
import type { DateRange } from './types';
import { getDatePresets } from './utils';

interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  monthOptions: DateRange[];
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({ value, onChange, monthOptions }) => {
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const presets = getDatePresets();

  const applyCustomRange = () => {
    if (customStart && customEnd) {
      onChange({
        label: `${customStart} to ${customEnd}`,
        value: 'custom',
        startDate: new Date(customStart),
        endDate: new Date(customEnd + 'T23:59:59')
      });
      setShowCustom(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Month tabs - scrollable on mobile */}
      <div className="overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
        <div className="flex gap-1 min-w-max">
          {monthOptions.map((month) => (
            <button
              key={month.value}
              onClick={() => onChange(month)}
              className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                value.value === month.value
                  ? 'bg-amber-500 text-black font-medium'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {month.label}
            </button>
          ))}
        </div>
      </div>

      {/* Quick presets and custom toggle */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-gray-500 text-sm">Quick:</span>
        {presets.map((preset) => (
          <button
            key={preset.value}
            onClick={() => onChange(preset)}
            className={`px-2 py-1 rounded text-sm transition-colors ${
              value.value === preset.value
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700 hover:text-white'
            }`}
          >
            {preset.label}
          </button>
        ))}
        <button
          onClick={() => setShowCustom(!showCustom)}
          className={`px-2 py-1 rounded text-sm transition-colors ${
            showCustom ? 'bg-purple-600 text-white' : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700 hover:text-white'
          }`}
        >
          📅 Custom
        </button>
      </div>

      {/* Custom date picker */}
      {showCustom && (
        <div className="flex flex-wrap gap-2 items-end p-3 bg-gray-700/50 rounded-lg">
          <div>
            <label className="text-white text-sm block mb-1">Start Date</label>
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm"
            />
          </div>
          <div>
            <label className="text-white text-sm block mb-1">End Date</label>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="bg-gray-800 border border-gray-600 rounded px-2 py-1 text-white text-sm"
            />
          </div>
          <button
            onClick={applyCustomRange}
            disabled={!customStart || !customEnd}
            className="bg-purple-600 hover:bg-purple-500 disabled:bg-gray-600 disabled:text-white text-white px-3 py-1 rounded text-sm"
          >
            Apply
          </button>
          <button
            onClick={() => setShowCustom(false)}
            className="text-gray-400 hover:text-white px-2 py-1 text-sm"
          >
            Cancel
          </button>
        </div>
      )}

      {/* Current selection display */}
      {value.startDate && value.endDate && (
        <p className="text-gray-500 text-sm">
          Showing: {value.startDate.toLocaleDateString()} - {value.endDate.toLocaleDateString()}
        </p>
      )}
    </div>
  );
};

export default DateRangePicker;

